import { prefix } from "../../design-tokens/meta";
import { matchers, validate } from "../../js/validate-props";
import { events } from "../../js/state-machine";
import { types as sortTypes } from "../../js/data-grid/sort-by-column";
import { componentClasses as theadClasses } from "./head/row";

export const componentName = "Table";

export const componentClasses = Object.freeze({
    base: {
        table: `${prefix}table`,
        tableColumnCustomSizing: `${prefix}table__cols-custom-sizing`,
        tableColumnPinned: `${prefix}table__col--pinned`,
        tableColumnLastPinned: `${prefix}table__col--pinned-last-column`,
        tableStickyHeader: `${prefix}table--sticky-header`,
        tableRow: `${prefix}tr`,
        tableHead: theadClasses.basic,
        tableHeadCell: `${prefix}th`,
        tableData: `${prefix}td`,
    },
    kind: {
        grid: "", //Empty because it is the default
        horizontal: `${prefix}table--horizontal`,
        horizontalAlternating: `${prefix}table--horizontal-alternating`,
        gridAlternating: `${prefix}table--grid-alternating`,
        freeform: `${prefix}table--freeform`,
        freeformAlternating: `${prefix}table--freeform-alternating`,
    },
    density: {
        compact: `${prefix}table--compact`,
        comfy: `${prefix}table--comfy`,
    },
});

export const views = Object.freeze({
    default: {
        classList: [`${componentName.wrapper}`],
    },
    pin: {
        pinned: {
            classList: [
                componentClasses.base.tableColumnPinned,
                `!${componentClasses.base.tableColumnLastPinned}`,
            ],
        },
        unpinned: {
            classList: [
                `!${componentClasses.base.tableColumnPinned}`,
                `!${componentClasses.base.tableColumnLastPinned}`,
            ],
        },
        lastColumn: {
            classList: [
                componentClasses.base.tableColumnPinned,
                componentClasses.base.tableColumnLastPinned,
            ],
        },
    },
});

export const defaultProps = Object.freeze({
    pagination: undefined,
    data: undefined,
    columns: undefined,
    pinnable: undefined,
    pin: {
        startColumn: undefined,
        pinLabel: undefined,
        unpinLabel: undefined,
    },
});

export const eventMap = Object.freeze({
    sort: events.sort(componentName),
    componentRender: events.componentRender(componentName),
    pageChange: events.pageChange(componentName),
    pageSizeChanged: events.pageSizeChanged(componentName),
});

export const isObjectWithValue = (item) => {
    return matchers.object(item) && matchers.or(matchers.string, matchers.number)(item.value);
};

export const validateContent = matchers.or(matchers.number, matchers.string, isObjectWithValue);

/**
 * Test if the items inside list matches validateContent function
 * @param {Array} list
 * @returns Boolean
 */
export const validateRows = (list) => {
    if (matchers.array(list) && list.length) {
        // Test if all items pass
        return list.filter(validateContent).length === list.length;
    }
    return false;
};

/**
 * Designed to be used with Array.map to test the content
 * and always return a objects like {value}
 * It should run after schema validation so we don't check the quality
 * of the **item**
 * @param {*} item String|Number|Object (object must have a property called value)
 * @returns {Object}
 */
export const parseCellsMap = (item) => {
    if (matchers.or(matchers.string, matchers.number)(item)) {
        return { value: item };
    }
    if (matchers.object(item)) {
        return item;
    }
    return null;
};

/**
 * Test if arguments follows the expected pattern to create the DataGrid
 * Expected values:
 * [[1],...]
 * [["1"],...]
 * [[{value:"1"}],...]
 * [[{value:1}],...]
 * @param {Array.<Array.<any>>} list List with rows
 * @return {Boolean}
 */
export const validateDataGrid = (list) => {
    // Has to be an array of rows
    if (matchers.array(list) && list.length) {
        // First level inside list must be another array (rows)
        const notEmpty = list.filter(matchers.array);
        // In the case of a smaller list means that some items were not good.
        if (notEmpty.length !== list.length) {
            return false;
        }
        // Checking rows quality
        const goodRows = list.filter(validateRows);
        return goodRows.length === notEmpty.length;
    }
    return false;
};

/**
 * Test schema for table columns
 * @param {Array.<Object>} list
 * @returns {Boolean}
 */
export const validateColumnData = (list) => {
    if (matchers.array(list)) {
        return list.reduce((allObject, item) => {
            const [pass] = validate(item, columnSchema);
            return (allObject = allObject && !!pass);
        }, true);
    }
    return false;
};

/**
 * Test schema for table pagination
 * @param {Object} config
 * @returns {Boolean}
 */
export const validatePagination = (config) => {
    if (matchers.object(config)) {
        const [pass, report] = validate(config, paginationSchema);
        if (!pass) {
            const strReport = Object.keys(report)
                .filter((prop) => !report[prop])
                .join(", ");
            console.warn(`${componentName} malformed pagination properties: ${strReport}`);
        }
        return pass;
    }
    return config === undefined ? true : false;
};

/**
 * Test schema for table pin
 * @param {Object} config
 * @returns {Boolean}
 */
export const validatePin = (config) => {
    if (matchers.object(config)) {
        const [pass, report] = validate(config, pinSchema);
        if (!pass) {
            const strReport = Object.keys(report)
                .filter((prop) => !report[prop])
                .join(", ");
            console.warn(`${componentName} malformed pin properties: ${strReport}`);
        }
        return pass;
    }
};

export const schema = Object.freeze({
    pagination: validatePagination,
    columns: validateColumnData,
    data: validateDataGrid,
    pin: validatePin,
    pinnable: matchers.or(matchers.undefined, matchers.boolean),
    // Resize handle commented until the a11y review
    // resizableColumns: matchers.or(matchers.undefined, matchers.boolean),
});

export const columnSchema = Object.freeze({
    value: matchers.or(matchers.string, matchers.number),
    align: matchers.or(matchers.undefined, matchers.match(/end/)),
    sortBy: matchers.or(matchers.undefined, (value) => {
        return value in sortTypes;
    }),
    minWidth: matchers.or(matchers.undefined, matchers.number),
    width: matchers.or(matchers.undefined, matchers.number),
    maxWidth: matchers.or(matchers.undefined, matchers.number),
    fit: matchers.or(matchers.undefined, matchers.boolean),
    // Resize handle commented until the a11y review
    // resizable: matchers.or(matchers.undefined, matchers.boolean),
});

export const pinSchema = Object.freeze({
    pinLabel: matchers.or(matchers.string, matchers.undefined),
    unpinLabel: matchers.or(matchers.string, matchers.undefined),
    startColumn: matchers.or(matchers.number, matchers.undefined),
});

export const paginationSchema = Object.freeze({
    topics: matchers.or(matchers.array, matchers.undefined),
    rowsPerPage: matchers.gt(0),
    currentPage: matchers.gt(0),
});
